/**
 * Centralized error handling for quota operations
 */

export interface QuotaError {
  type: 'QUOTA_EXCEEDED' | 'NETWORK_ERROR' | 'AUTH_ERROR' | 'UNKNOWN_ERROR';
  message: string;
  quotaType?: string;
  upgradeRequired?: boolean;
}

export class QuotaErrorHandler {
  /**
   * Handle quota check errors with consistent logging and user messaging
   */
  static handleQuotaCheckError(error: unknown, quotaType: string): QuotaError {
    console.error(`❌ [QuotaErrorHandler] Error checking quota for ${quotaType}:`, error);
    
    if (error instanceof Error) {
      if (error.message.includes('network') || error.message.includes('fetch')) {
        return {
          type: 'NETWORK_ERROR',
          message: 'Unable to verify quota limits. Please check your connection.',
          quotaType
        };
      }
      
      if (error.message.includes('auth') || error.message.includes('unauthorized')) {
        return {
          type: 'AUTH_ERROR',
          message: 'Authentication required to check quota limits.',
          quotaType
        };
      }
    }
    
    return {
      type: 'UNKNOWN_ERROR',
      message: 'An unexpected error occurred while checking quota limits.',
      quotaType
    };
  }

  /**
   * Handle quota increment errors
   */
  static handleQuotaIncrementError(error: unknown, quotaType: string): QuotaError {
    console.error(`❌ [QuotaErrorHandler] Error incrementing quota for ${quotaType}:`, error);
    
    return {
      type: 'UNKNOWN_ERROR',
      message: 'Failed to update quota usage. Your action was completed but usage may not be tracked.',
      quotaType
    };
  }

  /**
   * Create quota exceeded error
   */
  static createQuotaExceededError(quotaType: string, upgradeRequired: boolean = true): QuotaError {
    return {
      type: 'QUOTA_EXCEEDED',
      message: `You have reached your ${quotaType} limit. ${upgradeRequired ? 'Upgrade your plan to continue.' : ''}`,
      quotaType,
      upgradeRequired
    };
  }

  /**
   * Get user-friendly error message
   */
  static getUserMessage(error: QuotaError): string {
    switch (error.type) {
      case 'QUOTA_EXCEEDED':
        return error.message;
      case 'NETWORK_ERROR':
        return 'Please check your internet connection and try again.';
      case 'AUTH_ERROR':
        return 'Please sign in to continue.';
      default:
        return 'Something went wrong. Please try again.';
    }
  }

  /**
   * Check if error should block user action
   */
  static shouldBlockAction(error: QuotaError): boolean {
    return error.type === 'QUOTA_EXCEEDED';
  }

  /**
   * Check if error requires upgrade prompt
   */
  static shouldShowUpgradePrompt(error: QuotaError): boolean {
    return error.type === 'QUOTA_EXCEEDED' && (error.upgradeRequired ?? false);
  }
}
